/**
 * GitLab as a git provider — gitlab.com and self-managed (git sync Stage 1,
 * card 6 step 2). Sign-in with a personal access token (scopes: api,
 * write_repository); the device flow needs a registered OAuth app and is added
 * when one exists. Several accounts, on any number of servers, live in
 * services/git/gitAccounts.js; a workspace link names its account
 * (`git.accountId`), and every call here takes that link (`git`).
 *
 * API v4 through the Tauri HTTP adapter (no CORS, same as GitHub). Requests go
 * only to the server the account was added for.
 */
import axios from 'axios';
import { tauriAxiosAdapter } from '../../../utils/tauriAxiosAdapter';
import { WORKSPACE_TOPIC } from '../../../utils/appInfo';
import { getGitAccount, getGitAccountToken, listGitAccounts, hostOf } from '../gitAccounts';
import { ownersOf } from '../repoCatalog';

export const GITLAB_COM = 'https://gitlab.com';

function client(server, token) {
  return axios.create({
    baseURL: `${server}/api/v4`,
    adapter: tauriAxiosAdapter,
    timeout: 15000,
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json', 'Content-Type': 'application/json' },
  });
}

const enc = (owner, repo) => encodeURIComponent(`${owner}/${repo}`);

/** The account a link (or a picker choice) points at; the only GitLab account when it names none. */
async function accountFor(git) {
  if (git?.accountId) return getGitAccount(git.accountId);
  const all = await listGitAccounts('gitlab');
  const host = git?.url ? hostOf(git.url) : '';
  return (host ? all.find((a) => hostOf(a.server) === host) : null) || (all.length === 1 ? all[0] : null);
}

function mapProject(p) {
  const ns = p.namespace || {};
  const owner = ns.full_path || '';
  const top = owner.split('/')[0] || '';
  return {
    fullName: p.path_with_namespace,
    name: p.path,
    owner,
    // The owner filter groups by the top-level namespace: a user, or a group
    // with its subgroups (GitLab groups nest).
    ownerKey: top,
    ownerAvatar: top === owner ? ns.avatar_url || '' : '',
    ownerType: ns.kind === 'user' ? 'user' : 'org',
    private: p.visibility !== 'public',
    description: p.description || '',
    updatedAt: p.last_activity_at || '',
    url: p.http_url_to_repo,
    defaultBranch: p.default_branch || '',
  };
}

const PER_PAGE = 100;
const MAX_PAGES = 5; // 500 repositories / branches is plenty for a picker

/** Every page of a list endpoint, up to MAX_PAGES (stops at the first short page). */
async function allPages(http, path, params) {
  const out = [];
  for (let page = 1; page <= MAX_PAGES; page += 1) {
    const { data } = await http.get(path, { params: { ...params, per_page: PER_PAGE, page } });
    const items = Array.isArray(data) ? data : [];
    out.push(...items);
    if (items.length < PER_PAGE) break;
  }
  return out;
}

/** Escape a string for use inside a RegExp. */
const reEscape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const gitlab = {
  id: 'gitlab',
  label: 'GitLab',
  icon: 'gitlab',
  authKinds: ['token'],
  baseUrl: GITLAB_COM,
  selfHosted: true,
  abilities: { createRepo: true, listRepos: true, listBranches: true, openInBrowser: true, deviceFlow: false, tagWorkspace: true },
  /** GitLab takes any non-empty user name with a token; "oauth2" is the documented one. */
  httpsUsername: 'oauth2',
  notLinkedCode: 'GITLAB_NOT_LINKED',
  tokenHelp: 'GitLab → Preferences → Access tokens: scopes api and write_repository.',

  /** Who a token belongs to — used when adding an account. */
  async verifyToken(server, token) {
    const { data } = await client(server, token).get('/user');
    return { login: data?.username, name: data?.name, avatarUrl: data?.avatar_url, userId: data?.id };
  },

  async getToken(git) {
    const account = await accountFor(git);
    return account ? getGitAccountToken(account.id) : '';
  },

  async getAccount(git) {
    const account = await accountFor(git);
    return account
      ? { connected: true, login: account.login, avatarUrl: account.avatarUrl, server: account.server, id: account.id, userId: account.userId }
      : { connected: false, login: '', avatarUrl: '' };
  },

  /** GitLab's own no-reply form when the user id is known. */
  authorEmail(login, account) {
    // The host NAME — an e-mail domain has no port (a self-managed server on :8929).
    let host = 'gitlab.com';
    try {
      if (account?.server) host = new URL(account.server).hostname;
    } catch {
      /* keep gitlab.com */
    }
    if (!login) return 'noreply@apiboo.com';
    return account?.userId ? `${account.userId}-${login}@users.noreply.${host}` : `${login}@users.noreply.${host}`;
  },

  async repoUrl(owner, repo, git) {
    const account = await accountFor(git);
    return `${account?.server || GITLAB_COM}/${owner}/${repo}.git`;
  },

  webUrl(git) {
    return git?.url ? git.url.replace(/\.git$/, '') : '';
  },

  /**
   * "group/sub/project" or a URL on `server` → { owner, repo, url }. The owner
   * may be a nested group path.
   */
  parseRepo(input, server = GITLAB_COM) {
    const s = String(input || '').trim();
    if (!s) return null;
    const host = hostOf(server);
    const toRef = (path) => {
      const parts = path.replace(/\.git$/, '').replace(/\/+$/, '').split('/').filter(Boolean);
      if (parts.length < 2) return null;
      const repo = parts.pop();
      const owner = parts.join('/');
      return { owner, repo, url: `${server}/${owner}/${repo}.git` };
    };
    if (!s.includes('://') && !s.includes('@')) return toRef(s);
    const m = s.match(new RegExp(`${reEscape(host)}[/:](.+)$`, 'i'));
    return m ? toRef(m[1].split(/[?#]/)[0]) : null;
  },

  /**
   * Create the project if it is not there. Created with a README, for the same
   * reason as GitHub's auto_init: a push into an EMPTY repo fails under libgit2.
   */
  async ensureRepoExists(token, owner, repo, isPrivate, git) {
    const account = await accountFor(git);
    const api = client(account?.server || GITLAB_COM, token);
    try {
      await api.get(`/projects/${enc(owner, repo)}`);
      return true;
    } catch (e) {
      if (e.response?.status !== 404) throw e;
    }
    const body = { name: repo, path: repo, visibility: isPrivate ? 'private' : 'public', initialize_with_readme: true };
    if (owner && owner !== account?.login) {
      const { data: ns } = await api.get(`/namespaces/${encodeURIComponent(owner)}`);
      body.namespace_id = ns.id;
    }
    try {
      await api.post('/projects', body);
      return false;
    } catch (e) {
      if (e.response?.status === 400 && /taken|already/i.test(JSON.stringify(e.response?.data || ''))) return true;
      throw e;
    }
  },

  /**
   * Every project the user is a member of. `membership=true` covers projects
   * reached through a group (and its subgroups), not only direct membership —
   * checked 28.10.2026 against the GitLab API docs, so group projects were
   * always listed; only the picker had no group filter.
   */
  async listRepos(token, git) {
    const account = await accountFor(git);
    const list = await allPages(client(account?.server || GITLAB_COM, token), '/projects', {
      membership: true,
      order_by: 'last_activity_at',
    });
    return list.map(mapProject);
  },

  /** { repos, owners, notices }: projects plus the user's top-level groups (with avatars) for the filter. */
  async listRepoCatalog(token, git) {
    const account = await accountFor(git);
    const http = client(account?.server || GITLAB_COM, token);
    const repos = await gitlab.listRepos(token, git);
    let groups = [];
    try {
      groups = await allPages(http, '/groups', { min_access_level: 10, top_level_only: true });
    } catch {
      groups = []; // the filter still names the groups the projects belong to
    }
    const owners = ownersOf(repos, {
      login: account?.login || '',
      avatarUrl: account?.avatarUrl || '',
      extra: groups.map((g) => ({ key: g.full_path || g.path, login: g.full_path || g.path, avatarUrl: g.avatar_url || '', type: 'org' })),
    });
    return { repos, owners, notices: [] };
  },

  /** A project's branches: [{ name, protected }]. `owner` is the namespace path (groups nest). */
  async listBranches(token, owner, repo, git) {
    const account = await accountFor(git);
    const list = await allPages(client(account?.server || GITLAB_COM, token), `/projects/${enc(owner, repo)}/repository/branches`, {});
    return list.map((b) => ({ name: b.name, protected: Boolean(b.protected) }));
  },

  async listWorkspaceRepos(token, _login, git) {
    const account = await accountFor(git);
    const { data } = await client(account?.server || GITLAB_COM, token).get('/projects', {
      params: { membership: true, topic: WORKSPACE_TOPIC, order_by: 'last_activity_at', per_page: 100 },
    });
    return (data || []).map(mapProject);
  },

  /** Add the workspace topic (keeps the project's own). Best-effort — never throws. */
  async tagWorkspace(token, owner, repo, git) {
    try {
      const account = await accountFor(git);
      const api = client(account?.server || GITLAB_COM, token);
      const { data } = await api.get(`/projects/${enc(owner, repo)}`);
      const topics = Array.isArray(data?.topics) ? data.topics : [];
      if (topics.includes(WORKSPACE_TOPIC)) return;
      await api.put(`/projects/${enc(owner, repo)}`, { topics: [...topics, WORKSPACE_TOPIC] });
    } catch {
      // best-effort; tagging must not block sync
    }
  },
};

export default gitlab;
